import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/ui/ProductCard';
import './ProductsPage.css';

const categories = ['All', 'T-Shirt', 'Hoodie', 'Sweatshirt', 'Bag', 'Watch', 'Moneybag', 'Saree', 'Tops', 'Three Piece'];
const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
];

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = Number(searchParams.get('page') || 1);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    params.set('page', page);
    params.set('limit', 12);

    api.get(`/products?${params.toString()}`)
      .then(({ data }) => {
        setProducts(data.products);
        setTotal(data.total);
        setPages(data.pages);
      })
      .finally(() => setLoading(false));
  }, [category, search, sort, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <div className="prod-header-bg" />
        <div className="container">
          <h1 className="products-title">
            {category ? category : 'All'} <span className="gradient-text">Products</span>
          </h1>
          <p className="products-sub">{total} items found</p>
        </div>
      </div>

      <div className="container">
        {/* Filters Bar */}
        <div className="filters-bar">
          {/* Search */}
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input
              className="form-input search-input"
              placeholder="Search products..."
              value={search}
              onChange={e => setParam('search', e.target.value)}
            />
          </div>

          {/* Category Pills */}
          <div className="cat-pills">
            {categories.map(cat => (
              <button
                key={cat}
                className={`cat-pill ${(cat === 'All' ? !category : category === cat) ? 'active' : ''}`}
                onClick={() => setParam('category', cat === 'All' ? '' : cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Sort */}
          <select
            className="form-input sort-select"
            value={sort}
            onChange={e => setParam('sort', e.target.value)}
          >
            {sortOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="products-loading">
            <div className="spinner" />
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="grid-4">
              {products.map(p => <ProductCard key={p._id} product={p} />)}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  disabled={page === 1}
                  onClick={() => setParam('page', page - 1)}
                >
                  ← Prev
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={`page-btn ${p === page ? 'active' : ''}`}
                    onClick={() => setParam('page', p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="page-btn"
                  disabled={page === pages}
                  onClick={() => setParam('page', page + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-products">
            <div className="no-prod-icon">🔍</div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
